import { useEffect, useState } from 'react'
import {
  CardContent,
  CardHeader,
  Card,
  Button,
  Alert,
  CardActions,
  Grid,
  Box,
  List,
  ListItemText,
  ListItemButton,
  useTheme,
  Typography,
  AlertTitle
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Device, Product, Valve } from '../../types'
import { getDevice } from '../../services/deviceService'
import { QrReader } from 'react-qr-reader'
import DeviceCodeInput from '../DeviceCodeInput'
import { QrCode } from '@mui/icons-material'
import { DeviceType, ProductType } from '../../enums'
import DeviceDetachAlert from '../DeviceDetachAlert'
import ValveDetachHeaterAlert from '../ValveDetachHeaterAlert'
import { usePolicy } from '../../hooks'

interface ScanQrCodeOrEnterDeviceCodeStepProps {
  type?: DeviceType
  product?: Product
  withDetachAlert?: boolean
  withDetachHeaterAlert?: boolean
  initialDeviceName?: string
  onChange: (deviceName: string) => void
  onCancel: () => void
  onBack?: () => void
  onNext: () => void
}

export default function ScanQrCodeOrEnterDeviceCodeStep({
  type,
  product,
  withDetachAlert = true,
  withDetachHeaterAlert = true,
  initialDeviceName,
  onChange,
  onCancel,
  onBack,
  onNext
}: ScanQrCodeOrEnterDeviceCodeStepProps) {
  /**
   * The translation function.
   */
  const { t } = useTranslation('installation')

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  /**
   * Indicates if the QR-reader is enabled.
   */
  const [showQrReader, setShowQrReader] = useState<boolean>(false)

  /**
   * The short UUID of the device.
   */
  const [shortUUID, setShortUUID] = useState<string>('')

  /**
   * The error after loading the device.
   */
  const [deviceError, setDeviceError] = useState<Error>()

  /**
   * The selected device.
   */
  const [selectedDevice, setSelectedDevice] = useState<Device | undefined>()

  /**
   * Indicates if the detach heater alert should be shown.
   */
  const [showDetachHeaterAlert, setShowDetachHeaterAlert] = useState<boolean>(withDetachHeaterAlert)

  /**
   * Indicates if the alert for the wrong type of device should be shown.
   */
  const [showTypeMismatchErrorAlert, setShowTypeMismatchErrorAlert] = useState<boolean>(false)

  const [showProductMismatchErrorAlert, setShowProductMismatchErrorAlert] = useState<boolean>(false)

  /**
   * Indicates if the device loaded with an error.
   */
  const isDeviceError = deviceError !== undefined

  /**
   * Dismiss the detach heater alert.
   */
  function dismissDetachHeaterAlert() {
    setShowDetachHeaterAlert(false)
  }

  /**
   * Load the device by short UUID.
   */
  const loadDevice = async (shortUUID: string, controller?: AbortController) => {
    try {
      setDeviceError(undefined)
      setShowTypeMismatchErrorAlert(false)
      setShowProductMismatchErrorAlert(false)

      const device = await getDevice(shortUUID, { signal: controller?.signal })

      if (type && device.type !== type) {
        setShowTypeMismatchErrorAlert(true)
      } else if (product && device.product && device.product.id !== product.id) {
        setShowProductMismatchErrorAlert(true)
      } else {
        setSelectedDevice(device)
        onChange(device.name)
      }
    } catch (error: unknown) {
      setDeviceError(error as Error)
    }
  }

  const handleCancel = () => {
    setDeviceError(undefined)
    setSelectedDevice(undefined)
    setShortUUID('')
    onCancel()
  }

  const handleBack = () => {
    if (onBack) {
      onBack()
    }
  }

  const handleNext = () => {
    onNext()
  }

  /**
   * Load the device when short UUID changes.
   */
  useEffect(() => {
    if (shortUUID) {
      const controller = new AbortController()

      loadDevice(shortUUID, controller)

      return () => controller.abort()
    }
  }, [shortUUID])

  useEffect(() => {
    if (initialDeviceName && selectedDevice === undefined) {
      const controller = new AbortController()

      loadDevice(initialDeviceName, controller)

      return () => controller.abort()
    }

    return undefined
  }, [initialDeviceName, selectedDevice])

  return (
    <Card>
      <CardHeader title={t('scanQrCodeOrEnterDeviceCode')} />
      <CardContent sx={{ pt: 0 }}>
        <Grid container spacing={6}>
          {isDeviceError && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle sx={{ m: 0 }}>{t('deviceWasNotFound')}</AlertTitle>
              </Alert>
            </Grid>
          )}

          {showTypeMismatchErrorAlert && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle sx={{ mt: 0 }}>{t('deviceWasFoundButTypeWasMismatch')}</AlertTitle>
              </Alert>
            </Grid>
          )}

          {showProductMismatchErrorAlert && (
            <Grid item xs={12}>
              <Alert severity="error">
                <AlertTitle sx={{ mt: 0 }}>
                  {t('deviceWasFoundButAttachedToAnotherProduct')}
                </AlertTitle>
              </Alert>
            </Grid>
          )}

          {selectedDevice === undefined ? (
            <Grid
              item
              xs={12}
              sx={{
                textAlign: 'center'
              }}
            >
              {!showQrReader ? (
                <Box
                  p={6}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{
                    minHeight: '25vh',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 1
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <QrCode sx={{ fontSize: 64 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">
                        {t('installation:enableCameraToReadQrCode')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => setShowQrReader(true)}
                      >
                        {t('installation:enableCamera')}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box sx={{ backgroundColor: 'black' }}>
                  <Box
                    sx={{
                      margin: 'auto',
                      maxWidth: '800px'
                    }}
                  >
                    <QrReader
                      scanDelay={500}
                      constraints={{ facingMode: 'environment' }}
                      onResult={(result) => {
                        if (result) {
                          const regex = new RegExp(/^https:\/\/qr\.spine-electric\.io\/[0-9]{6}$/)
                          if (regex.test(result.getText())) {
                            setShortUUID(result.getText().substring(29))
                          }
                        }
                      }}
                      videoContainerStyle={{
                        backgroundColor: 'black'
                      }}
                    />
                  </Box>
                </Box>
              )}

              <DeviceCodeInput
                onChange={(deviceCode) => {
                  if (deviceCode.length === 6) {
                    setShortUUID(deviceCode)
                  }
                }}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Alert severity="success">
                  <AlertTitle sx={{ mt: 0 }}>{t('deviceWasFound')}</AlertTitle>
                </Alert>

                <List>
                  {selectedDevice?.asset?.name &&
                    selectedDevice.asset.name !== selectedDevice.name && (
                      <ListItemButton>
                        <ListItemText
                          primary={selectedDevice.asset.name}
                          secondary={t('asset:name')}
                        />
                      </ListItemButton>
                    )}

                  {selectedDevice?.asset?.company?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.company.name}
                        secondary={t('asset:company')}
                      />
                    </ListItemButton>
                  )}

                  {selectedDevice?.asset?.project?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.project.name}
                        secondary={t('asset:project')}
                      />
                    </ListItemButton>
                  )}

                  {selectedDevice?.asset?.level?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.level.name}
                        secondary={t('asset:level')}
                      />
                    </ListItemButton>
                  )}

                  {selectedDevice?.asset?.lift?.name && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.asset.lift.name}
                        secondary={t('asset:lift')}
                      />
                    </ListItemButton>
                  )}

                  <ListItemButton>
                    <ListItemText primary={selectedDevice.name} secondary={t('device:name')} />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText
                      primary={selectedDevice.shortUUID}
                      secondary={t('device:shortUUID')}
                    />
                  </ListItemButton>

                  {(isSuperadmin || isManufacturer) && (
                    <ListItemButton>
                      <ListItemText
                        primary={selectedDevice.simId ?? '-'}
                        secondary={t('device:simId')}
                      />
                    </ListItemButton>
                  )}

                  <ListItemButton>
                    <ListItemText
                      primary={selectedDevice.firmwareVersion ?? '-'}
                      secondary={t('device:firmwareVersion')}
                    />
                  </ListItemButton>

                  <ListItemButton>
                    <ListItemText
                      primary={selectedDevice.hardwareVersion ?? '-'}
                      secondary={t('device:hardwareVersion')}
                    />
                  </ListItemButton>
                </List>

                {withDetachAlert && selectedDevice?.asset?.projectId && (
                  <DeviceDetachAlert device={selectedDevice} />
                )}
              </Grid>

              {withDetachHeaterAlert &&
                showDetachHeaterAlert &&
                selectedDevice.type === DeviceType.VALVE &&
                selectedDevice.heaterId !== null && (
                  <Grid item xs={12}>
                    <ValveDetachHeaterAlert
                      valve={selectedDevice as Valve}
                      onCancel={dismissDetachHeaterAlert}
                      afterUpdate={setSelectedDevice}
                    />
                  </Grid>
                )}
            </>
          )}
        </Grid>
      </CardContent>

      <CardActions sx={{ justifyContent: 'flex-end' }}>
        {onBack && (
          <Button variant="text" color="inherit" onClick={handleBack}>
            {t('installation:back')}
          </Button>
        )}

        <Button variant="text" color="inherit" onClick={handleCancel}>
          {t('cancel')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={selectedDevice === undefined}
          onClick={handleNext}
        >
          {t('next')}
        </Button>
      </CardActions>
    </Card>
  )
}
